import type { Accessor, Component } from "solid-js";
import { createMemo, For, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useI18n } from "@/api/i18n";
import { SkillBadge } from "@/components/shared/SkillBadge";
import { Badge } from "@/components/ui/badge";
import { categoryLabel } from "@/domains/freelance/taxonomy/store/taxonomy-store";
import { OfferSubcategoryChips } from "@/domains/job-offer/components/offer-subcategory-chips";
import { REMOTE_ICONS } from "@/domains/job-offer/constants/remote-icons";
import type { JobOfferV2WithCompanyId } from "@/domains/job-offer/types";
import { formatJobCompensation } from "@/libs/format";

interface JobOfferBadgesProps {
  job: JobOfferV2WithCompanyId;
  isInactive: Accessor<boolean>;
  maxSkills?: number;
}

export const JobOfferBadges: Component<JobOfferBadgesProps> = (props) => {
  const { t } = useI18n();
  const isInactive = props.isInactive;

  const slicedSkills = createMemo(() => {
    const max = props.maxSkills ?? 8;
    return props.job.skills?.slice(0, max);
  });

  const remainingSkillsCount = createMemo(() => {
    const total = props.job.skills?.length ?? 0;
    const shown = slicedSkills()?.length ?? 0;
    return total > shown ? total - shown : 0;
  });

  return (
    <>
      <div class="flex items-center gap-2 flex-wrap">
        <Show when={props.job.payment_type}>
          {(pt) => (
            <Badge
              variant={isInactive() ? "secondary" : "info"}
              size="sm"
              class="rounded-full"
            >
              {t(`joblist.jobOffer.paymentType.${pt()}`)}:{" "}
              {formatJobCompensation(props.job)}
            </Badge>
          )}
        </Show>
        <Show when={props.job.job_category}>
          <Badge
            variant={isInactive() ? "secondary" : "light-primary"}
            size="sm"
            class="rounded-full"
          >
            {categoryLabel(props.job.job_category)}
          </Badge>
        </Show>
        <OfferSubcategoryChips
          category={props.job.job_category}
          keys={props.job.job_subcategory}
          inactive={isInactive()}
        />

        <Show when={props.job.remote_policy}>
          {(rp) => (
            <Badge variant="secondary" size="sm" class="rounded-full">
              <Dynamic component={REMOTE_ICONS[rp()]} size={14} class="mr-1" />
              {t(`common.remote_policy_options.${rp()}`)}
              <Show
                when={
                  rp() !== "remote" &&
                  props.job.location &&
                  props.job.location.length > 0
                }
              >
                {" • "}
                <span class="text-xs ml-1">
                  {props.job.location?.join(", ")}
                </span>
              </Show>
            </Badge>
          )}
        </Show>
      </div>
      <div class="flex items-center gap-2 flex-wrap">
        <For each={slicedSkills()}>
          {(skill) => <SkillBadge skill={skill} />}
        </For>
        <Show when={remainingSkillsCount() > 0}>
          <p class="text-xs text-gray-500">
            {t("common.more_skills", { count: remainingSkillsCount() })}
          </p>
        </Show>
      </div>
    </>
  );
};
